// @/components/reusable/button.jsx
// Catalyst-based Button — TW4 + HeadlessUI 2.2
// Modes: solid | outline | plain  ·  Colors: dark, light, primary, red, orange, zinc, blue, ...
// Legacy variants: danger → solid+red, warning → solid+orange, secondary → outline, text → plain unstyled

import React, { forwardRef } from "react";
import * as Headless from "@headlessui/react";
import clsx from "clsx";
import { Link } from "gatsby";

// ============================================================================
// CATALYST STYLES
// ============================================================================

const styles = {
  base: [
    // Base
    "relative isolate inline-flex items-center justify-center gap-x-2 rounded-xl border text-base/6 font-fono font-semibold tracking-tighter",
    // Sizing
    "px-[calc(--spacing(4)+1px)] py-[calc(--spacing(2)+1px)] sm:px-[calc(--spacing(6)+1px)] sm:py-[calc(--spacing(2)+1px)] text-xs/5 sm:text-sm/6",
    // Focus — only visible on keyboard navigation, not mouse click
    "focus:outline-hidden focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500",
    // Disabled
    "data-disabled:opacity-30",
    // Icon
    "*:data-[slot=icon]:-mx-0.5 *:data-[slot=icon]:size-4 *:data-[slot=icon]:shrink-0 *:data-[slot=icon]:self-center *:data-[slot=icon]:text-(--btn-icon) sm:*:data-[slot=icon]:size-5 forced-colors:[--btn-icon:ButtonText] forced-colors:data-hover:[--btn-icon:ButtonText]",
    // Cursor
    "cursor-pointer",
    // Transition
    "transition-colors duration-150",
  ],
  solid: [
    // Optical border, implemented as the button background to avoid corner artifacts
    "border-transparent bg-(--btn-border)",
    // Dark mode: border is rendered on `after` so background is set to button background
    "dark:bg-(--btn-bg)",
    // Button background, implemented as foreground layer to stack on top of pseudo-border layer
    "before:absolute before:inset-0 before:-z-10 before:rounded-[calc(var(--radius-xl)-1px)] before:bg-(--btn-bg)",
    // Drop shadow, applied to the inset `before` layer so it blends with the border
    "before:shadow-xs",
    // Background color is moved to control and shadow is removed in dark mode so hide `before` pseudo
    "dark:before:hidden",
    // Dark mode: Subtle white outline is applied using a border
    "dark:border-white/5",
    // Shim/overlay, inset to match button foreground and used for hover state + highlight shadow
    "after:absolute after:inset-0 after:-z-10 after:rounded-[calc(var(--radius-xl)-1px)]",
    // Inner highlight shadow
    "after:shadow-[inset_0_1px_--theme(--color-white/15%)]",
    // White overlay on hover
    "data-active:after:bg-(--btn-hover-overlay) data-hover:after:bg-(--btn-hover-overlay) hover:after:bg-(--btn-hover-overlay)",
    // Dark mode: `after` layer expands to cover entire button
    "dark:after:-inset-px dark:after:rounded-xl",
    // Disabled
    "data-disabled:before:shadow-none data-disabled:after:shadow-none",
  ],
  outline: [
    // Border — matches Input component
    "border-zinc-200 data-hover:border-zinc-300 hover:border-zinc-300 text-zinc-950",
    // Background + shadow via before: pseudo — same as Input component
    "bg-transparent before:absolute before:inset-px before:rounded-[calc(var(--radius-xl)-1px)] before:bg-zinc-50/50 before:shadow-xs before:-z-10",
    // Hover — darken the before: pseudo
    "data-hover:before:bg-zinc-100/60 hover:before:bg-zinc-100/60 data-active:before:bg-zinc-100/80",
    // Dark mode
    "dark:border-white/15 dark:text-white dark:before:bg-white/5 dark:before:shadow-none dark:data-hover:border-white/20 dark:hover:border-white/20 dark:data-active:bg-white/5",
    // Icon
    "[--btn-icon:var(--color-zinc-500)] data-active:[--btn-icon:var(--color-zinc-700)] data-hover:[--btn-icon:var(--color-zinc-700)] hover:[--btn-icon:var(--color-zinc-700)] dark:data-active:[--btn-icon:var(--color-zinc-400)] dark:data-hover:[--btn-icon:var(--color-zinc-400)] dark:hover:[--btn-icon:var(--color-zinc-400)]",
  ],
  plain: [
    // Base
    "border-transparent text-zinc-950 data-active:bg-zinc-950/5 data-hover:bg-zinc-950/5 hover:bg-zinc-950/5",
    // Dark mode
    "dark:text-white dark:data-active:bg-white/10 dark:data-hover:bg-white/10 dark:hover:bg-white/10",
    // Icon
    "[--btn-icon:var(--color-zinc-500)] data-active:[--btn-icon:var(--color-zinc-700)] data-hover:[--btn-icon:var(--color-zinc-700)] hover:[--btn-icon:var(--color-zinc-700)] dark:[--btn-icon:var(--color-zinc-500)] dark:data-active:[--btn-icon:var(--color-zinc-400)] dark:data-hover:[--btn-icon:var(--color-zinc-400)] dark:hover:[--btn-icon:var(--color-zinc-400)]",
  ],
  // Input-style — identical to outline but kept as explicit alias for semantic clarity
  // Use variant="input" in filter bars, toolbars, and alongside form inputs
  input: null, // resolved via LEGACY_MAP → outline
  colors: {
    "dark/zinc": [
      "text-white [--btn-bg:var(--color-zinc-900)] [--btn-border:var(--color-zinc-950)]/90 [--btn-hover-overlay:var(--color-white)]/10",
      "dark:text-white dark:[--btn-bg:var(--color-zinc-600)] dark:[--btn-hover-overlay:var(--color-white)]/5",
      "[--btn-icon:var(--color-zinc-400)] data-active:[--btn-icon:var(--color-zinc-300)] data-hover:[--btn-icon:var(--color-zinc-300)]",
    ],
    dark: [
      "text-white [--btn-bg:var(--color-zinc-900)] [--btn-border:var(--color-zinc-950)]/90 [--btn-hover-overlay:var(--color-white)]/10",
      "dark:[--btn-hover-overlay:var(--color-white)]/5 dark:[--btn-bg:var(--color-zinc-800)]",
      "[--btn-icon:var(--color-zinc-400)] data-active:[--btn-icon:var(--color-zinc-300)] data-hover:[--btn-icon:var(--color-zinc-300)]",
    ],
    light: [
      "text-zinc-950 [--btn-bg:white] [--btn-border:var(--color-zinc-950)]/10 [--btn-hover-overlay:var(--color-zinc-950)]/2.5 data-active:[--btn-border:var(--color-zinc-950)]/15 data-hover:[--btn-border:var(--color-zinc-950)]/15",
      "dark:text-white dark:[--btn-hover-overlay:var(--color-white)]/5 dark:[--btn-bg:var(--color-zinc-800)]",
      "[--btn-icon:var(--color-zinc-500)] data-active:[--btn-icon:var(--color-zinc-700)] data-hover:[--btn-icon:var(--color-zinc-700)] dark:[--btn-icon:var(--color-zinc-500)] dark:data-active:[--btn-icon:var(--color-zinc-400)] dark:data-hover:[--btn-icon:var(--color-zinc-400)]",
    ],
    white: [
      "text-zinc-950 [--btn-bg:white] [--btn-border:var(--color-zinc-950)]/10 [--btn-hover-overlay:var(--color-zinc-950)]/2.5 data-active:[--btn-border:var(--color-zinc-950)]/15 data-hover:[--btn-border:var(--color-zinc-950)]/15",
      "dark:[--btn-hover-overlay:var(--color-zinc-950)]/5",
      "[--btn-icon:var(--color-zinc-400)] data-active:[--btn-icon:var(--color-zinc-500)] data-hover:[--btn-icon:var(--color-zinc-500)]",
    ],
    zinc: [
      "text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-zinc-600)] [--btn-border:var(--color-zinc-700)]/90",
      "dark:[--btn-hover-overlay:var(--color-white)]/5",
      "[--btn-icon:var(--color-zinc-400)] data-active:[--btn-icon:var(--color-zinc-300)] data-hover:[--btn-icon:var(--color-zinc-300)]",
    ],
    blue: [
      "text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-blue-600)] [--btn-border:var(--color-blue-700)]/90",
      "[--btn-icon:var(--color-blue-400)] data-active:[--btn-icon:var(--color-blue-300)] data-hover:[--btn-icon:var(--color-blue-300)]",
    ],
    red: [
      "text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-red-600)] [--btn-border:var(--color-red-700)]/90",
      "[--btn-icon:var(--color-red-300)] data-active:[--btn-icon:var(--color-red-200)] data-hover:[--btn-icon:var(--color-red-200)]",
    ],
    orange: [
      "text-orange-600 [--btn-hover-overlay:transparent] [--btn-bg:var(--color-orange-50)] [--btn-border:var(--color-orange-100)] data-hover:[--btn-bg:var(--color-orange-100)] data-active:[--btn-bg:var(--color-orange-100)]",
      "[--btn-icon:var(--color-orange-600)] data-active:[--btn-icon:var(--color-orange-700)] data-hover:[--btn-icon:var(--color-orange-700)]",
    ],
    rose: [
      "text-rose-600 [--btn-hover-overlay:transparent] [--btn-bg:var(--color-rose-50)] [--btn-border:var(--color-rose-100)] data-hover:[--btn-bg:var(--color-rose-100)] data-active:[--btn-bg:var(--color-rose-100)]",
      "[--btn-icon:var(--color-rose-600)] data-active:[--btn-icon:var(--color-rose-700)] data-hover:[--btn-icon:var(--color-rose-700)]",
    ],
    green: [
      "text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-green-600)] [--btn-border:var(--color-green-700)]/90",
      "[--btn-icon:var(--color-white)]/60 data-active:[--btn-icon:var(--color-white)]/80 data-hover:[--btn-icon:var(--color-white)]/80",
    ],
    emerald: [
      "text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-emerald-600)] [--btn-border:var(--color-emerald-700)]/90",
      "[--btn-icon:var(--color-white)]/60 data-active:[--btn-icon:var(--color-white)]/80 data-hover:[--btn-icon:var(--color-white)]/80",
    ],
    // Light teal — same subtle hover pattern as orange and notification banners
    teal: [
      "text-teal-700 [--btn-hover-overlay:transparent] [--btn-bg:var(--color-teal-50)] [--btn-border:var(--color-teal-100)] data-hover:[--btn-bg:var(--color-teal-100)] data-active:[--btn-bg:var(--color-teal-100)]",
      "[--btn-icon:var(--color-teal-700)] data-active:[--btn-icon:var(--color-teal-800)] data-hover:[--btn-icon:var(--color-teal-800)]",
    ],
    indigo: [
      "text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-indigo-500)] [--btn-border:var(--color-indigo-600)]/90",
      "[--btn-icon:var(--color-indigo-300)] data-active:[--btn-icon:var(--color-indigo-200)] data-hover:[--btn-icon:var(--color-indigo-200)]",
    ],
    violet: [
      "text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-violet-500)] [--btn-border:var(--color-violet-600)]/90",
      "[--btn-icon:var(--color-violet-300)] data-active:[--btn-icon:var(--color-violet-200)] data-hover:[--btn-icon:var(--color-violet-200)]",
    ],
    amber: [
      "text-amber-950 [--btn-hover-overlay:var(--color-white)]/25 [--btn-bg:var(--color-amber-400)] [--btn-border:var(--color-amber-500)]/80",
      "[--btn-icon:var(--color-amber-600)]",
    ],
    cyan: [
      "text-cyan-950 [--btn-bg:var(--color-cyan-300)] [--btn-border:var(--color-cyan-400)]/80 [--btn-hover-overlay:var(--color-white)]/25",
      "[--btn-icon:var(--color-cyan-500)]",
    ],
  },
};

// ============================================================================
// LEGACY VARIANT → CATALYST MAPPING
// ============================================================================

// Maps old variant names to { mode, color } so existing code keeps working.
// `danger` → solid red, `warning` → solid orange, `secondary` → outline, etc.
const LEGACY_MAP = {
  danger: { mode: "solid", color: "rose" },
  warning: { mode: "solid", color: "orange" },
  secondary: { mode: "outline", color: null },
  input: { mode: "outline", color: null },
};

// ============================================================================
// SIZE VARIANTS
// ============================================================================

const sizes = {
  xs: "px-2.5! py-1! text-xs/5! *:data-[slot=icon]:!size-3.5",
  sm: "px-4! py-2! text-sm/5! *:data-[slot=icon]:!size-4",
  md: "", // default — uses base sizing
  lg: "px-10! py-2.5! text-base/6!",
  icon: "px-2.5! py-2.5!",
};

// ============================================================================
// RESOLVE CLASSES
// ============================================================================

function resolveClasses({ variant = "solid", color, size = "md", disabled, className }) {
  // Text variant — special case, no Catalyst layering
  if (variant === "text") {
    return clsx(
      styles.base,
      "px-0! py-0! border-none! bg-transparent! shadow-none! rounded-none! ring-0! text-zinc-950 dark:text-zinc-100",
      sizes[size] || "",
      disabled && "opacity-50 pointer-events-none",
      className,
    );
  }

  // Resolve legacy variants
  const legacy = LEGACY_MAP[variant];
  const mode = legacy?.mode ?? variant; // solid | outline | plain
  const resolvedColor = color ?? legacy?.color ?? null;

  // Build class list
  const modeStyles = styles[mode] || styles.solid;
  const colorStyles = mode === "solid" && resolvedColor
    ? styles.colors[resolvedColor] || styles.colors.dark
    : null;

  // Solid with no explicit color → default dark
  const defaultColor = mode === "solid" && !resolvedColor
    ? styles.colors.dark
    : null;

  return clsx(
    styles.base,
    modeStyles,
    colorStyles || defaultColor,
    sizes[size] || "",
    disabled && "pointer-events-none",
    className,
  );
}

// ============================================================================
// BUTTON COMPONENT
// ============================================================================

/**
 * Button — Catalyst-based button with full color system.
 *
 * Modes (variant prop):
 *   "solid"   — filled button with color layering (default)
 *   "outline" — bordered button, transparent bg
 *   "plain"   — no border, subtle hover
 *   "text"    — unstyled text button, no padding
 *
 * Legacy variants (still work, mapped internally):
 *   "danger"    → solid + red
 *   "warning"   → solid + orange
 *   "secondary" → outline
 *   "input"     → outline
 *
 * Colors (only applies to solid mode):
 *   "dark" (default), "light", "white", "zinc", "blue", "red", "orange",
 *   "green", "emerald", "indigo", "violet", "amber", "cyan", "dark/zinc"
 *
 * Sizes: "xs" | "sm" | "md" (default) | "lg" | "icon"
 *
 * @param {string} [to] — Gatsby route or external URL
 */
export const Button = forwardRef(function Button(
  { variant = "solid", color, size = "md", disabled, className, to, children, ...props },
  ref,
) {
  const classes = resolveClasses({ variant, color, size, disabled, className });

  // External link
  if (to && (to.startsWith("http://") || to.startsWith("https://"))) {
    return (
      <a
        {...props}
        href={to}
        className={classes}
        ref={ref}
        target="_blank"
        rel="noopener noreferrer"
      >
        <TouchTarget>{children}</TouchTarget>
      </a>
    );
  }

  // Internal Gatsby link
  if (to) {
    return (
      <Link {...props} to={to} className={classes} ref={ref}>
        <TouchTarget>{children}</TouchTarget>
      </Link>
    );
  }

  // Standard button
  return (
    <Headless.Button
      {...props}
      disabled={disabled}
      className={clsx(classes, disabled && "cursor-not-allowed!")}
      ref={ref}
    >
      <TouchTarget>{children}</TouchTarget>
    </Headless.Button>
  );
});

// ============================================================================
// CLASS UTILITY — for DropdownButton / PopoverButton / MenuButton
// ============================================================================

/**
 * Returns the resolved class string for a given variant + color + size.
 * Use when you need button styling on a non-Button element (e.g. DropdownButton).
 */
export function getButtonClasses({ variant = "solid", color, size = "md", className } = {}) {
  return resolveClasses({ variant, color, size, className });
}

// ============================================================================
// BUTTON GROUP
// ============================================================================

export function ButtonGroup({ className, children, ...props }) {
  return (
    <div
      {...props}
      className={clsx(className, "flex justify-center sm:justify-end gap-5")}
    >
      {children}
    </div>
  );
}

// ============================================================================
// TOUCH TARGET — expand hit area to 44×44px on touch devices
// ============================================================================

export function TouchTarget({ children }) {
  return (
    <>
      <span
        className="absolute top-1/2 left-1/2 size-[max(100%,2.75rem)] -translate-x-1/2 -translate-y-1/2 pointer-fine:hidden"
        aria-hidden="true"
      />
      {children}
    </>
  );
}
